import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { BusinessDtoKeysEnum } from '@zaoblako/shared/modules/business/enum/business-dto-keys.enum';
import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { CompanyFindDto, type CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import { NomenclatureItemFindDto, type NomenclatureItemFindInterface } from '@zaoblako/shared/modules/catalog/dtos/nomenclature/nomenclature-item-find.dto';
import { UserGroupFindDto, type UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { EavEntityTypeFindDto, type EavEntityTypeFindInterface } from '@zaoblako/shared/modules/eav/dtos/entity-type/eav-entity-type-find.dto';

import * as yup from 'yup';

/** Deal type */
export interface DealTypeFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;
  /** Deal type creation date and time */
  created: Date;
  /** Deal type last update date and time */
  updated: Date;
  /** Deal type soft deletion date and time */
  deleted?: Date;
  /** Deal type display name */
  name: string;
  /** If `true` then this deal type is default for rental deals and cannot be deleted */
  isRentDefault?: boolean;
  /** Default company */
  defaultCompany?: CompanyFindInterface;
  /** Default nomenclature items */
  defaultNomenclatures?: NomenclatureItemFindInterface[];
  /** Responsible user */
  responsible?: UserFindInterface;
  /** Auditor users */
  auditorUsers?: UserFindInterface[];
  /** Auditor groups */
  auditorGroups?: UserGroupFindInterface[];
  /** Fields template (EAV entity type) */
  eavEntityType?: EavEntityTypeFindInterface;
  /** User who created this deal type */
  createdBy?: UserFindInterface;
}

/** Deal type - DTO schema */
export const DealTypeFindDto = SerdeDtoClass.create<DealTypeFindInterface>(BusinessDtoKeysEnum.DEAL_TYPE_FIND_DTO, {
  id: {
    sortable: true,
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    label: 'Обновлён',
    serializer: yup.date(),
  },
  deleted: {
    optional: true,
    label: 'Дата удаления',
    serializer: yup.date(),
  },
  name: {
    sortable: true,
    label: 'Название',
    serializer: yup.string(),
  },
  isRentDefault: {
    optional: true,
    label: 'Тип по умолчанию для аренды',
    serializer: yup.boolean(),
  },
  defaultCompany: {
    sortable: true,
    optional: true,
    label: 'Компания по-умолчанию',
    nested: CompanyFindDto,
    serializer: (_, nested) => nested,
  },
  defaultNomenclatures: {
    optional: true,
    label: 'Номенклатура по-умолчанию',
    nested: NomenclatureItemFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  responsible: {
    sortable: true,
    optional: true,
    label: 'Ответственный',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  auditorUsers: {
    optional: true,
    label: 'Пользователи-аудиторы',
    nested: UserFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  auditorGroups: {
    optional: true,
    label: 'Группы-аудиторы',
    nested: UserGroupFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  eavEntityType: {
    sortable: true,
    optional: true,
    label: 'Шаблон полей',
    nested: EavEntityTypeFindDto,
    serializer: (_, nested) => nested,
  },
  createdBy: {
    optional: true,
    label: 'Создатель',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
});

